<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-use-before-define */
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPayloadViewer from 'dashboard/components-next/LaisPayloadViewer.vue';
import LaisActionReasonModal from 'dashboard/components-next/LaisActionReasonModal.vue';
import MudancasAPI, { normalizeMudancasError } from 'dashboard/api/mudancas';

const emit = defineEmits(['mercadoTabState']);
const PAGE_SIZE = 25;
const route = useRoute();
const router = useRouter();

const INITIAL_FILTERS = {
  busca: '',
  tipo: '',
  status: '',
  prioridade: '',
  auditStatus: '',
  usuario_email: '',
  imovel_codigo: '',
  desde: '',
  ate: '',
  sortBy: 'data_solicitacao',
  sortDirection: 'desc',
};

const routeQueryValue = key => {
  const value = route.query[key];
  return Array.isArray(value) ? value[0] || '' : value || '';
};

const initialFiltersFromRoute = () => ({
  ...INITIAL_FILTERS,
  auditStatus: normalizeOptionValue(
    routeQueryValue('auditStatus'),
    auditOptions
  ),
  usuario_email: normalizeTextFilter(
    routeQueryValue('usuario_email') || routeQueryValue('user')
  ),
  desde: normalizeDateFilter(
    routeQueryValue('desde') || routeQueryValue('updatedSince')
  ),
  ate: normalizeDateFilter(
    routeQueryValue('ate') || routeQueryValue('updatedUntil')
  ),
});

const tipoOptions = [
  { value: 'preco', label: 'Preço' },
  { value: 'descricao', label: 'Descrição' },
  { value: 'fotos', label: 'Fotos' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'disponibilidade', label: 'Disponibilidade' },
  { value: 'exclusao', label: 'Exclusão' },
  { value: 'inclusao', label: 'Inclusão' },
  { value: 'caracteristicas', label: 'Características' },
  { value: 'documentacao', label: 'Documentação' },
  { value: 'contato', label: 'Contato' },
  { value: 'outros', label: 'Outros' },
];

const statusOptions = [
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'aprovada', label: 'Aprovada' },
  { value: 'rejeitada', label: 'Rejeitada' },
  { value: 'concluida', label: 'Concluída' },
];

const prioridadeOptions = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];

const auditOptions = [
  { value: 'unverified', label: 'Não verificada' },
  { value: 'matched', label: 'Confirmada' },
  { value: 'unmatched', label: 'Sem correspondência' },
  { value: 'stale', label: 'Desatualizada' },
];

const sortOptions = [
  { value: 'data_solicitacao', label: 'Data da solicitação' },
  { value: 'extraido_em', label: 'Extração pela IA' },
  { value: 'tipo_alteracao', label: 'Tipo' },
  { value: 'status', label: 'Status' },
  { value: 'audit_status', label: 'Auditoria' },
  { value: 'prioridade', label: 'Prioridade' },
  { value: 'confianca', label: 'Confiança' },
];

const tableColumns = [
  { key: 'mudanca', label: 'Mudança', sortBy: 'tipo_alteracao' },
  { key: 'imovel_codigo', label: 'Imóvel' },
  { key: 'solicitante', label: 'Solicitante' },
  { key: 'campo_alterado', label: 'Campo' },
  { key: 'valor_anterior', label: 'Antes' },
  { key: 'valor_novo', label: 'Depois' },
  { key: 'status', label: 'Status', sortBy: 'status' },
  { key: 'audit_status', label: 'Auditoria', sortBy: 'audit_status' },
  { key: 'prioridade', label: 'Prioridade', sortBy: 'prioridade' },
  { key: 'confianca', label: 'Confiança', sortBy: 'confianca' },
  {
    key: 'data_solicitacao',
    label: 'Solicitada em',
    sortBy: 'data_solicitacao',
  },
  { key: 'extraido_em', label: 'Extraída em', sortBy: 'extraido_em' },
];

const inputClass =
  'h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none transition placeholder:text-lais-text-muted focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50';
const selectClass =
  'h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] font-semibold text-lais-text outline-none transition focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50';
const compactButtonClass =
  'inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:bg-lais-primary-50 hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50';
const primaryCompactButtonClass =
  'inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-primary bg-lais-primary px-4 text-[13px] font-semibold text-white transition hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:opacity-50';

const items = ref([]);
const filters = ref(initialFiltersFromRoute());
const pageMeta = ref({
  page: 1,
  size: PAGE_SIZE,
  total: 0,
  totalPages: 1,
});
const isLoading = ref(false);
const showFilters = ref(false);
const listError = ref(null);
const errorMessage = ref('');
const lastLoadedAt = ref(null);
const selectedMudanca = ref(null);
const isDetailLoading = ref(false);
const detailErrorMessage = ref('');
const actionLedger = ref([]);
const isActionLedgerLoading = ref(false);
const actionErrorMessage = ref('');
const actionSuccessMessage = ref('');
const isActionSubmitting = ref(false);
const actionModal = ref({ show: false, decision: null });

const formatter = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
});
const shortTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});
const numberFormatter = new Intl.NumberFormat('pt-BR');
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 0,
});

const labelMaps = {
  tipo: new Map(tipoOptions.map(option => [option.value, option.label])),
  status: new Map(statusOptions.map(option => [option.value, option.label])),
  prioridade: new Map(
    prioridadeOptions.map(option => [option.value, option.label])
  ),
  auditStatus: new Map(
    auditOptions.map(option => [option.value, option.label])
  ),
  sortBy: new Map(sortOptions.map(option => [option.value, option.label])),
};

const activeFiltersCount = computed(
  () =>
    [
      filters.value.busca,
      filters.value.tipo,
      filters.value.status,
      filters.value.prioridade,
      filters.value.auditStatus,
      filters.value.usuario_email,
      filters.value.imovel_codigo,
      filters.value.desde,
      filters.value.ate,
    ].filter(Boolean).length
);

const activeFilterChips = computed(() => {
  const chips = [];
  const entries = [
    ['busca', 'Busca'],
    ['tipo', 'Tipo'],
    ['status', 'Status'],
    ['prioridade', 'Prioridade'],
    ['auditStatus', 'Auditoria'],
    ['usuario_email', 'Usuário'],
    ['imovel_codigo', 'Imóvel'],
    ['desde', 'De'],
    ['ate', 'Até'],
  ];

  entries.forEach(([key, label]) => {
    const value = filters.value[key];
    if (!value) return;
    chips.push({
      key,
      label: `${label}: ${filterLabel(key, value)}`,
    });
  });
  return chips;
});

const pageStart = computed(() => {
  if (!pageMeta.value.total) return 0;
  return (pageMeta.value.page - 1) * pageMeta.value.size + 1;
});

const pageEnd = computed(() =>
  Math.min(pageMeta.value.page * pageMeta.value.size, pageMeta.value.total)
);

const lastUpdateLabel = computed(() => {
  if (!lastLoadedAt.value) return 'ainda não carregado';
  return shortTimeFormatter.format(lastLoadedAt.value);
});

const isListUnavailable = computed(() => listError.value?.isUnavailable);
const errorTitle = computed(
  () => listError.value?.title || 'Não foi possível carregar mudanças'
);
const activeMercadoTabKey = computed(() =>
  routeQueryValue('auditStatus') === 'unverified' ? 'auditoria' : 'mudancas'
);
const mercadoTabStatus = computed(() => {
  if (isLoading.value) return 'loading';
  if (!errorMessage.value) return 'ready';
  return isListUnavailable.value ? 'unavailable' : 'error';
});
const mercadoTabState = computed(() => ({
  key: activeMercadoTabKey.value,
  status: mercadoTabStatus.value,
  message: errorMessage.value,
}));

const summaryCards = computed(() => [
  {
    key: 'total',
    label: 'Total filtrado',
    value: number(pageMeta.value.total),
    icon: 'i-lucide-database',
    tone: 'bg-lais-primary-50 text-lais-primary ring-lais-primary-100',
  },
  {
    key: 'page',
    label: 'Nesta página',
    value: number(items.value.length),
    icon: 'i-lucide-table-2',
    tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  },
  {
    key: 'updated',
    label: 'Atualizado',
    value: lastUpdateLabel.value,
    icon: 'i-lucide-clock-3',
    tone: 'bg-amber-50 text-amber-700 ring-amber-100',
  },
]);

const isDetailModalOpen = computed(() => !!selectedMudanca.value);
const selectedMudancaTitle = computed(() =>
  display(selectedMudanca.value?.descricao_alteracao, 'Detalhe da mudança')
);
const provenanceRows = computed(() => {
  const item = selectedMudanca.value || {};
  return [
    { label: 'Fonte', value: display(item.fonte_nome, 'imobiliaria-api') },
    { label: 'Tipo de fonte', value: display(item.fonte_tipo, 'Solicitação') },
    { label: 'Data da fonte', value: formatDate(item.fonte_data) },
    { label: 'Confiança', value: confidenceLabel(item.confianca) },
  ];
});
const ruleExplanation = computed(() =>
  display(
    selectedMudanca.value?.explicacao_regra,
    'Regra não informada pelo serviço de mudanças.'
  )
);
const detailMetaRows = computed(() => {
  const item = selectedMudanca.value || {};
  return [
    { label: 'ID', value: display(item.id) },
    { label: 'Imóvel', value: display(item.imovel_codigo) },
    { label: 'Campo', value: display(item.campo_alterado) },
    { label: 'Solicitante', value: requesterName(item) },
    { label: 'E-mail', value: display(requesterEmail(item)) },
    { label: 'E-mail de origem', value: display(item.email_id) },
    { label: 'Status', value: statusLabel(item.status) },
    { label: 'Auditoria', value: auditLabel(item.audit_status) },
    { label: 'Prioridade', value: prioridadeLabel(item.prioridade) },
    { label: 'Confiança', value: confidenceLabel(item.confianca) },
    { label: 'Solicitada em', value: formatDate(item.data_solicitacao) },
    { label: 'Extraída em', value: formatDate(item.extraido_em) },
  ];
});
const detailRawPayload = computed(() => {
  const item = selectedMudanca.value || {};
  return (
    item.raw_payload ||
    item.rawPayload ||
    item.payload ||
    item.metadata ||
    item.detalhes ||
    null
  );
});
const hasDetailRawPayload = computed(
  () =>
    detailRawPayload.value !== null &&
    detailRawPayload.value !== undefined &&
    detailRawPayload.value !== ''
);
const detailPayloadRedactedKeys = [
  'authorization',
  'password',
  'secret',
  'token',
  'cpf',
  'cnpj',
  'phone',
  'telefone',
  'whatsapp',
  'email',
  'document',
  'matricula',
  'matrícula',
  'pix',
  'bank',
  'account',
];

const decisionTerminalStatuses = ['aprovada', 'rejeitada', 'concluida'];
const canDecideSelectedMudanca = computed(
  () =>
    selectedMudanca.value &&
    !decisionTerminalStatuses.includes(selectedMudanca.value.status)
);
const actionModalCopy = computed(() => {
  const decision = actionModal.value.decision;
  const copies = {
    accept: {
      title: 'Aceitar mudança',
      description:
        'Registra a aprovação desta mudança e envia a decisão ao serviço de origem.',
      consequence:
        'Confirme apenas depois de revisar origem, regra e comparação antes/depois.',
      reasonLabel: 'Motivo do aceite',
      reasonPlaceholder:
        'Ex.: conferido com fonte oficial e dado atual coerente.',
      acknowledgeLabel: 'Revisei a origem, a regra e o diff desta mudança.',
      confirmText: 'Aceitar mudança',
      confirmVariant: 'primary',
    },
    reject: {
      title: 'Recusar mudança',
      description:
        'Registra a recusa desta mudança e envia a decisão ao serviço de origem.',
      consequence:
        'A recusa deve explicar por que o dado novo não deve ser aplicado.',
      reasonLabel: 'Motivo da recusa',
      reasonPlaceholder: 'Ex.: fonte divergente, valor antigo ainda válido.',
      acknowledgeLabel: 'Revisei o diff e tenho evidência para recusar.',
      confirmText: 'Recusar mudança',
      confirmVariant: 'danger',
    },
    comment: {
      title: 'Comentar mudança',
      description: 'Registra um comentário operacional no ledger local.',
      consequence:
        'O comentário não altera o status da mudança nem chama o serviço externo.',
      reasonLabel: 'Comentário com motivo',
      reasonPlaceholder: 'Ex.: aguardando validação de matrícula pelo time.',
      acknowledgeLabel: '',
      confirmText: 'Registrar comentário',
      confirmVariant: 'primary',
    },
  };
  return copies[decision] || copies.comment;
});

function number(value) {
  return numberFormatter.format(Number(value || 0));
}

function filterLabel(key, value) {
  if (key === 'tipo') return tipoLabel(value);
  if (key === 'status') return statusLabel(value);
  if (key === 'prioridade') return prioridadeLabel(value);
  if (key === 'auditStatus') return auditLabel(value);
  return value;
}

function optionLabel(mapKey, value) {
  if (!value) return '-';
  return labelMaps[mapKey]?.get(value) || value;
}

function tipoLabel(value) {
  return optionLabel('tipo', value);
}

function statusLabel(value) {
  return optionLabel('status', value);
}

function prioridadeLabel(value) {
  return optionLabel('prioridade', value);
}

function auditLabel(value) {
  return optionLabel('auditStatus', value);
}

function display(value, fallback = '-') {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
}

function normalizeTextFilter(value) {
  return String(value || '').trim();
}

function normalizeOptionValue(value, options) {
  const normalized = normalizeTextFilter(value);
  if (!normalized) return '';

  return options.some(option => option.value === normalized) ? normalized : '';
}

function normalizeDateFilter(value) {
  const normalized = normalizeTextFilter(value);
  if (!normalized) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const date = new Date(`${normalized}T00:00:00`);
    return Number.isNaN(date.getTime()) ? '' : normalized;
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function normalizeDetailPayload(payload, fallbackItem) {
  const detail = payload?.item || payload?.data || payload?.payload || payload;
  if (!detail || Array.isArray(detail) || typeof detail !== 'object') {
    return fallbackItem;
  }

  return { ...fallbackItem, ...detail };
}

function requesterName(item) {
  return display(item.usuario_nome || item.usuario?.nome || item.usuario_email);
}

function requesterEmail(item) {
  return item.usuario_email || item.usuario?.email || '';
}

function confidenceLabel(value) {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return '-';
  return percentFormatter.format(numeric);
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return formatter.format(date);
}

function toStartOfDay(value) {
  const normalized = normalizeDateFilter(value);
  if (!normalized) return '';
  return new Date(`${normalized}T00:00:00`).toISOString();
}

function toEndOfDay(value) {
  const normalized = normalizeDateFilter(value);
  if (!normalized) return '';
  return new Date(`${normalized}T23:59:59.999`).toISOString();
}

function statusTone(value) {
  return (
    {
      pendente: 'bg-amber-50 text-amber-800 ring-amber-200',
      em_analise: 'bg-blue-50 text-blue-800 ring-blue-200',
      aprovada: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
      rejeitada: 'bg-red-50 text-red-800 ring-red-200',
      concluida: 'bg-slate-100 text-slate-700 ring-slate-200',
    }[value] || 'bg-lais-bg-page text-lais-text-muted ring-lais-border'
  );
}

function auditTone(value) {
  return (
    {
      matched: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
      unmatched: 'bg-red-50 text-red-800 ring-red-200',
      stale: 'bg-amber-50 text-amber-800 ring-amber-200',
      unverified: 'bg-slate-100 text-slate-700 ring-slate-200',
    }[value] || 'bg-lais-bg-page text-lais-text-muted ring-lais-border'
  );
}

function prioridadeTone(value) {
  return (
    {
      baixa: 'bg-slate-100 text-slate-700 ring-slate-200',
      media: 'bg-blue-50 text-blue-800 ring-blue-200',
      alta: 'bg-amber-50 text-amber-800 ring-amber-200',
      urgente: 'bg-red-50 text-red-800 ring-red-200',
    }[value] || 'bg-lais-bg-page text-lais-text-muted ring-lais-border'
  );
}

function sortIcon(column) {
  if (filters.value.sortBy !== column.sortBy) {
    return 'i-lucide-chevrons-up-down text-lais-text-muted';
  }
  return filters.value.sortDirection === 'asc'
    ? 'i-lucide-arrow-up text-lais-primary'
    : 'i-lucide-arrow-down text-lais-primary';
}

function sortByColumn(column) {
  if (!column.sortBy) return;
  if (filters.value.sortBy === column.sortBy) {
    filters.value.sortDirection =
      filters.value.sortDirection === 'asc' ? 'desc' : 'asc';
  } else {
    filters.value.sortBy = column.sortBy;
    filters.value.sortDirection = 'asc';
  }
  pageMeta.value.page = 1;
  loadItems();
}

function buildParams() {
  const sortBy = normalizeOptionValue(filters.value.sortBy, sortOptions);
  const sortDirection = filters.value.sortDirection === 'asc' ? 'asc' : 'desc';
  const direction = sortDirection === 'asc' ? '' : '-';

  return {
    page: pageMeta.value.page,
    size: PAGE_SIZE,
    busca: normalizeTextFilter(filters.value.busca),
    tipo: normalizeOptionValue(filters.value.tipo, tipoOptions),
    status: normalizeOptionValue(filters.value.status, statusOptions),
    prioridade: normalizeOptionValue(
      filters.value.prioridade,
      prioridadeOptions
    ),
    auditStatus: normalizeOptionValue(filters.value.auditStatus, auditOptions),
    usuario_email: normalizeTextFilter(filters.value.usuario_email),
    imovel_codigo: normalizeTextFilter(filters.value.imovel_codigo),
    desde: toStartOfDay(filters.value.desde),
    ate: toEndOfDay(filters.value.ate),
    order: `${direction}${sortBy || INITIAL_FILTERS.sortBy}`,
  };
}

function setRouteQueryValue(query, key, value) {
  const normalized = String(value || '').trim();
  if (normalized) {
    query[key] = normalized;
  } else {
    delete query[key];
  }
}

function normalizedQuerySnapshot(query) {
  return Object.keys(query)
    .sort()
    .map(key => [key, Array(query[key]).flat().join('|')]);
}

function replaceRouteQueryIfChanged(query) {
  if (
    JSON.stringify(normalizedQuerySnapshot(query)) ===
    JSON.stringify(normalizedQuerySnapshot(route.query))
  ) {
    return;
  }
  router.replace({ name: route.name, params: route.params, query });
}

function syncMercadoSharedQuery() {
  const query = { ...route.query };
  setRouteQueryValue(query, 'auditStatus', filters.value.auditStatus);
  setRouteQueryValue(query, 'usuario_email', filters.value.usuario_email);
  setRouteQueryValue(query, 'desde', filters.value.desde);
  setRouteQueryValue(query, 'ate', filters.value.ate);
  delete query.user;
  delete query.updatedSince;
  delete query.updatedUntil;
  replaceRouteQueryIfChanged(query);
}

async function loadItems() {
  isLoading.value = true;
  listError.value = null;
  errorMessage.value = '';
  try {
    const { data } = await MudancasAPI.list(buildParams());
    const total = Number(data?.total || 0);
    const size = Number(data?.size || PAGE_SIZE);
    const page = Number(data?.page || pageMeta.value.page);
    items.value = Array.isArray(data?.items) ? data.items : [];
    pageMeta.value = {
      page,
      size,
      total,
      totalPages: Math.max(1, Math.ceil(total / Math.max(size, 1))),
    };
    lastLoadedAt.value = new Date();
  } catch (error) {
    const normalizedError = normalizeMudancasError(error);
    listError.value = normalizedError;
    errorMessage.value = normalizedError.message;
    items.value = [];
    pageMeta.value = {
      page: 1,
      size: PAGE_SIZE,
      total: 0,
      totalPages: 1,
    };
  } finally {
    isLoading.value = false;
  }
}

function applyFilters() {
  pageMeta.value.page = 1;
  syncMercadoSharedQuery();
  loadItems();
}

function applyRouteFilters() {
  const routeFilters = initialFiltersFromRoute();
  const routeFilterKeys = ['auditStatus', 'usuario_email', 'desde', 'ate'];
  const hasChanged = routeFilterKeys.some(
    key => filters.value[key] !== routeFilters[key]
  );
  if (!hasChanged) return;

  filters.value = {
    ...filters.value,
    ...routeFilterKeys.reduce((nextFilters, key) => {
      nextFilters[key] = routeFilters[key];
      return nextFilters;
    }, {}),
  };
  applyFilters();
}

function resetFilters() {
  filters.value = { ...INITIAL_FILTERS };
  pageMeta.value.page = 1;
  syncMercadoSharedQuery();
  loadItems();
}

function removeFilter(key) {
  filters.value[key] = '';
  applyFilters();
}

function refreshAll() {
  loadItems();
}

async function openDetail(item) {
  selectedMudanca.value = item;
  detailErrorMessage.value = '';
  actionErrorMessage.value = '';
  actionSuccessMessage.value = '';
  loadActionLedger(item.id);
  isDetailLoading.value = true;
  try {
    const { data } = await MudancasAPI.item(item.id);
    selectedMudanca.value = normalizeDetailPayload(data, item);
  } catch (error) {
    detailErrorMessage.value =
      error.response?.data?.error ||
      'Não foi possível carregar o detalhe completo. Exibindo dados da lista.';
  } finally {
    isDetailLoading.value = false;
  }
}

function closeDetail() {
  selectedMudanca.value = null;
  detailErrorMessage.value = '';
  isDetailLoading.value = false;
  actionLedger.value = [];
  actionErrorMessage.value = '';
  actionSuccessMessage.value = '';
  closeActionModal();
}

async function loadActionLedger(itemId) {
  if (!itemId) return;
  isActionLedgerLoading.value = true;
  try {
    const { data } = await MudancasAPI.actionLedger(itemId);
    actionLedger.value = Array.isArray(data?.items) ? data.items : [];
  } catch {
    actionLedger.value = [];
  } finally {
    isActionLedgerLoading.value = false;
  }
}

function openActionModal(decision) {
  actionErrorMessage.value = '';
  actionSuccessMessage.value = '';
  actionModal.value = { show: true, decision };
}

function closeActionModal() {
  actionModal.value = { show: false, decision: null };
}

function actionIdempotencyKey(decision, itemId) {
  return `dashboard:mudancas:${itemId}:${decision}:${Date.now()}`;
}

function actionStatusPatch(decision) {
  if (decision === 'accept')
    return { status: 'aprovada', audit_status: 'matched' };
  if (decision === 'reject') {
    return { status: 'rejeitada', audit_status: 'unmatched' };
  }
  return {};
}

function updateListedMudanca(updatedItem) {
  items.value = items.value.map(item =>
    item.id === updatedItem.id ? { ...item, ...updatedItem } : item
  );
}

function prependLedgerEntry(entry) {
  if (!entry?.id) return;
  actionLedger.value = [
    entry,
    ...actionLedger.value.filter(current => current.id !== entry.id),
  ];
}

function applyActionResult(decision, data) {
  const current = selectedMudanca.value || {};
  const patch = {
    ...actionStatusPatch(decision),
    ...(data?.mudanca || {}),
  };
  const updatedItem = { ...current, ...patch };
  selectedMudanca.value = updatedItem;
  updateListedMudanca(updatedItem);
  prependLedgerEntry(data?.ledger);
  actionSuccessMessage.value = data?.idempotent_replay
    ? 'Ação já registrada anteriormente; ledger reaproveitado.'
    : 'Ação registrada com segurança no ledger.';
}

function actionErrorFrom(error) {
  const payload = error?.response?.data || {};
  if (payload.code === 'mudancas_service_unavailable') {
    return 'Serviço de mudanças indisponível. A tentativa foi registrada no ledger; use uma nova chave para reenviar depois.';
  }
  return (
    payload.message ||
    payload.error ||
    'Não foi possível registrar a ação desta mudança.'
  );
}

function submitMudancaAction(decision, itemId, payload) {
  if (decision === 'accept') return MudancasAPI.accept(itemId, payload);
  if (decision === 'reject') return MudancasAPI.reject(itemId, payload);
  return MudancasAPI.comment(itemId, payload);
}

async function performMudancaAction({ reason }) {
  const decision = actionModal.value.decision;
  const itemId = selectedMudanca.value?.id;
  if (!decision || !itemId) return;

  isActionSubmitting.value = true;
  actionErrorMessage.value = '';
  actionSuccessMessage.value = '';
  try {
    const payload = {
      reason,
      comment: decision === 'comment' ? reason : undefined,
      idempotency_key: actionIdempotencyKey(decision, itemId),
      metadata: {
        surface: 'dashboard_mudancas',
        schema_version: 'mudancas-action-ui-v1',
      },
    };
    const { data } = await submitMudancaAction(decision, itemId, payload);
    applyActionResult(decision, data);
    closeActionModal();
  } catch (error) {
    actionErrorMessage.value = actionErrorFrom(error);
    const ledger = error?.response?.data?.ledger;
    if (ledger?.id) prependLedgerEntry(ledger);
  } finally {
    isActionSubmitting.value = false;
  }
}

function actionLabel(value) {
  return (
    {
      accept: 'Aceite',
      reject: 'Recusa',
      comment: 'Comentário',
    }[value] || display(value)
  );
}

function actionStatusLabel(value) {
  return (
    {
      succeeded: 'Registrada',
      failed: 'Falhou',
    }[value] || display(value)
  );
}

function actionStatusTone(value) {
  return value === 'succeeded'
    ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : 'bg-red-50 text-red-800 ring-red-200';
}

function goToPage(page) {
  if (page < 1 || page > pageMeta.value.totalPages || isLoading.value) return;
  pageMeta.value.page = page;
  loadItems();
}

watch(
  () => [
    route.query.auditStatus,
    route.query.usuario_email,
    route.query.user,
    route.query.desde,
    route.query.ate,
    route.query.updatedSince,
    route.query.updatedUntil,
  ],
  applyRouteFilters
);
watch(mercadoTabState, state => emit('mercadoTabState', state), {
  immediate: true,
});

onMounted(loadItems);
</script>

<template>
  <section
    class="block h-full w-full min-w-0 max-w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-[#f7f8fb] font-redHatText [contain:paint]"
  >
    <LaisHeader
      class="shrink-0"
      title="Mudanças"
      helper="E-mails processados pela IA no imobiliaria-api, organizados pela tabela estruturada de solicitações."
      title-badge="Dados ao vivo"
    >
      <template #actions>
        <LaisFilterButton
          :active="showFilters || activeFiltersCount > 0"
          @click="showFilters = !showFilters"
        >
          Filtros
          <span
            v-if="activeFiltersCount"
            class="ml-1 inline-flex min-w-5 justify-center rounded-full bg-lais-primary px-1.5 text-[11px] text-white"
          >
            {{ activeFiltersCount }}
          </span>
        </LaisFilterButton>
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="isLoading"
          @click="refreshAll"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <form
      class="w-full min-w-0 max-w-full shrink-0 overflow-x-hidden border-y border-lais-border bg-white px-4 py-3 sm:px-6 lg:px-10"
      @submit.prevent="applyFilters"
    >
      <div
        class="grid min-w-0 gap-2 xl:grid-cols-[minmax(18rem,1fr)_auto] xl:items-center"
      >
        <label class="relative min-w-0 flex-1">
          <span
            class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
            aria-hidden="true"
          />
          <input
            v-model="filters.busca"
            aria-label="Buscar mudanças"
            class="h-10 w-full rounded-lais border border-lais-border bg-white pl-10 pr-3 text-[14px] text-lais-text outline-none transition placeholder:text-lais-text-muted focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50"
            placeholder="Buscar descrição, solicitante, imóvel, campo, valor ou e-mail ID"
          />
        </label>

        <div
          class="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:flex xl:flex-wrap xl:justify-end"
        >
          <select v-model="filters.tipo" class="sm:w-52" :class="selectClass">
            <option value="">Todos os tipos</option>
            <option
              v-for="option in tipoOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <select v-model="filters.status" class="sm:w-48" :class="selectClass">
            <option value="">Todos os status</option>
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <button
            type="submit"
            class="col-span-2 sm:col-span-1"
            :class="primaryCompactButtonClass"
          >
            <span class="i-lucide-search size-4" aria-hidden="true" />
            Buscar
          </button>
          <button
            type="button"
            :class="compactButtonClass"
            :disabled="!activeFiltersCount"
            @click="resetFilters"
          >
            <span class="i-lucide-rotate-ccw size-4" aria-hidden="true" />
            Limpar
          </button>
        </div>
      </div>

      <div
        v-if="activeFilterChips.length"
        class="mt-3 flex gap-2 overflow-x-auto pb-1 no-scrollbar"
      >
        <button
          v-for="chip in activeFilterChips"
          :key="chip.key"
          type="button"
          class="inline-flex shrink-0 items-center gap-2 rounded-full border border-lais-border bg-lais-bg-page px-3 py-1.5 text-[12px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
          @click="removeFilter(chip.key)"
        >
          {{ chip.label }}
          <span class="i-lucide-x size-3.5" aria-hidden="true" />
        </button>
      </div>
    </form>

    <LaisFilterBar :show="showFilters" class="shrink-0">
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:min-w-[170px] sm:flex-1"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Imóvel
        </span>
        <input
          v-model="filters.imovel_codigo"
          :class="inputClass"
          placeholder="Ex.: LAAP1234"
          @keyup.enter="applyFilters"
        />
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:min-w-[190px] sm:flex-1"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Usuário
        </span>
        <input
          v-model="filters.usuario_email"
          :class="inputClass"
          placeholder="email@dominio.com"
          @keyup.enter="applyFilters"
        />
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[150px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Prioridade
        </span>
        <select v-model="filters.prioridade" :class="selectClass">
          <option value="">Todas</option>
          <option
            v-for="option in prioridadeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[190px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Auditoria
        </span>
        <select v-model="filters.auditStatus" :class="selectClass">
          <option value="">Todas</option>
          <option
            v-for="option in auditOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[150px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          De
        </span>
        <input v-model="filters.desde" type="date" :class="inputClass" />
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[150px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Até
        </span>
        <input v-model="filters.ate" type="date" :class="inputClass" />
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[190px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Ordenar por
        </span>
        <select v-model="filters.sortBy" :class="selectClass">
          <option
            v-for="option in sortOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label
        class="flex w-full min-w-0 flex-col gap-1 sm:w-auto sm:min-w-[120px]"
      >
        <span
          class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Direção
        </span>
        <select v-model="filters.sortDirection" :class="selectClass">
          <option value="desc">Desc.</option>
          <option value="asc">Asc.</option>
        </select>
      </label>
      <button
        type="button"
        class="w-full sm:w-auto"
        :class="primaryCompactButtonClass"
        @click="applyFilters"
      >
        Aplicar filtros
      </button>
    </LaisFilterBar>

    <main
      class="w-full min-w-0 max-w-full bg-lais-bg-page px-4 pb-24 pt-4 sm:px-6 md:pb-8 lg:px-10"
    >
      <div class="grid gap-3 sm:grid-cols-3 lg:hidden">
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="flex items-center gap-3 rounded-lais border border-lais-border bg-white px-4 py-3 shadow-lais-card"
        >
          <span
            class="grid size-10 shrink-0 place-items-center rounded-full ring-1"
            :class="card.tone"
          >
            <span class="size-4" :class="card.icon" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <span
              class="block text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              {{ card.label }}
            </span>
            <strong
              class="block truncate text-[22px] font-semibold leading-tight text-lais-text"
            >
              {{ card.value }}
            </strong>
          </span>
        </div>
      </div>

      <div
        class="mt-4 flex flex-col gap-2 rounded-lais border border-lais-border bg-white px-4 py-3 shadow-lais-card sm:flex-row sm:items-center sm:justify-between lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:shadow-none"
      >
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="text-[16px] font-semibold text-lais-text">
              Solicitações extraídas
            </h2>
            <span
              class="inline-flex items-center gap-1 rounded-full border border-lais-border bg-lais-bg-page px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
            >
              <span class="i-lucide-database size-3" aria-hidden="true" />
              imobiliaria-api / solicitacoes
            </span>
          </div>
          <p class="mt-0.5 text-[12px] text-lais-text-muted">
            Exibindo {{ number(pageStart) }}-{{ number(pageEnd) }} de
            {{ number(pageMeta.total) }} registros · última atualização:
            {{ lastUpdateLabel }}
          </p>
        </div>
        <p class="text-[13px] font-semibold text-lais-text-muted">
          {{ labelMaps.sortBy.get(filters.sortBy) }} ·
          {{ filters.sortDirection === 'asc' ? 'ascendente' : 'descendente' }}
        </p>
      </div>

      <div
        v-if="errorMessage"
        class="mt-4 flex flex-col gap-3 rounded-lais border px-4 py-3 text-[13px] shadow-lais-card sm:flex-row sm:items-center sm:justify-between"
        :class="
          isListUnavailable
            ? 'border-amber-200 bg-amber-50 text-amber-800'
            : 'border-red-100 bg-red-50 text-red-700'
        "
        role="alert"
      >
        <span class="flex min-w-0 items-start gap-3">
          <span
            class="mt-0.5 size-4 shrink-0"
            :class="
              isListUnavailable ? 'i-lucide-cloud-off' : 'i-lucide-circle-alert'
            "
            aria-hidden="true"
          />
          <span class="min-w-0">
            <strong class="block font-semibold">{{ errorTitle }}</strong>
            <span class="mt-0.5 block font-medium">{{ errorMessage }}</span>
          </span>
        </span>
        <button
          v-if="isListUnavailable"
          type="button"
          class="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-lais border border-amber-300 bg-white px-3 text-[12px] font-semibold text-amber-800 transition hover:border-amber-400 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isLoading"
          @click="refreshAll"
        >
          <span class="i-lucide-refresh-cw size-3.5" aria-hidden="true" />
          Atualizar
        </button>
      </div>

      <div class="mt-4 grid min-w-0 gap-3 overflow-hidden lg:hidden">
        <template v-if="isLoading">
          <div
            v-for="index in 4"
            :key="index"
            class="animate-pulse rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
          >
            <div class="h-4 w-2/3 rounded bg-lais-bg-page" />
            <div class="mt-3 h-3 w-full rounded bg-lais-bg-page" />
            <div class="mt-2 h-3 w-4/5 rounded bg-lais-bg-page" />
          </div>
        </template>

        <LaisCard v-else-if="isListUnavailable">
          <div class="grid place-items-center py-10 text-center">
            <span
              class="i-lucide-cloud-off mx-auto size-8 text-amber-700"
              aria-hidden="true"
            />
            <h2 class="mt-3 text-[15px] font-semibold text-lais-text">
              Serviço de mudanças indisponível
            </h2>
            <p
              class="mx-auto mt-1 max-w-sm text-[13px] font-medium text-lais-text-muted"
            >
              {{ errorMessage }}
            </p>
            <button
              type="button"
              class="mt-3 inline-flex items-center gap-2 text-[13px] font-semibold text-lais-primary underline disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isLoading"
              @click="refreshAll"
            >
              <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
              Atualizar listagem
            </button>
          </div>
        </LaisCard>

        <LaisCard v-else-if="!items.length">
          <div class="grid place-items-center py-10 text-center">
            <span
              class="i-lucide-search-x mx-auto size-8 text-lais-text-muted"
              aria-hidden="true"
            />
            <h2 class="mt-3 text-[15px] font-semibold text-lais-text">
              Nenhuma mudança encontrada
            </h2>
            <button
              v-if="activeFiltersCount"
              type="button"
              class="mt-3 text-[13px] font-semibold text-lais-primary underline"
              @click="resetFilters"
            >
              Limpar filtros
            </button>
          </div>
        </LaisCard>

        <template v-else>
          <article
            v-for="item in items"
            :key="item.id"
            role="button"
            tabindex="0"
            class="overflow-hidden rounded-lais border border-lais-border bg-white shadow-lais-card transition hover:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary-100"
            :aria-label="`Ver detalhe da mudança ${item.id}`"
            @click="openDetail(item)"
            @keydown.enter.prevent="openDetail(item)"
            @keydown.space.prevent="openDetail(item)"
          >
            <header
              class="flex items-start justify-between gap-3 border-b border-lais-border-soft px-4 pb-3 pt-4"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <span
                    class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-bold text-lais-primary"
                  >
                    {{ tipoLabel(item.tipo_alteracao) }}
                  </span>
                  <span class="text-[11px] font-semibold text-lais-text-muted">
                    E-mail #{{ item.email_id }}
                  </span>
                </div>
                <h3
                  class="mt-2 line-clamp-2 text-[15px] font-semibold leading-snug text-lais-text"
                >
                  {{ display(item.descricao_alteracao, 'Sem descrição') }}
                </h3>
              </div>
              <span
                class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                :class="statusTone(item.status)"
              >
                {{ statusLabel(item.status) }}
              </span>
            </header>

            <dl class="grid grid-cols-2 gap-2 px-4 py-3">
              <div class="rounded-lais-sm border border-lais-border-soft p-2">
                <dt class="text-[11px] font-semibold text-lais-text-muted">
                  Imóvel
                </dt>
                <dd
                  class="mt-0.5 truncate text-[13px] font-semibold text-lais-text"
                >
                  {{ display(item.imovel_codigo) }}
                </dd>
              </div>
              <div class="rounded-lais-sm border border-lais-border-soft p-2">
                <dt class="text-[11px] font-semibold text-lais-text-muted">
                  Solicitante
                </dt>
                <dd
                  class="mt-0.5 truncate text-[13px] font-semibold text-lais-text"
                >
                  {{ requesterName(item) }}
                </dd>
              </div>
              <div class="rounded-lais-sm border border-lais-border-soft p-2">
                <dt class="text-[11px] font-semibold text-lais-text-muted">
                  Campo
                </dt>
                <dd
                  class="mt-0.5 truncate text-[13px] font-semibold text-lais-text"
                >
                  {{ display(item.campo_alterado) }}
                </dd>
              </div>
              <div class="rounded-lais-sm border border-lais-border-soft p-2">
                <dt class="text-[11px] font-semibold text-lais-text-muted">
                  Confiança
                </dt>
                <dd
                  class="mt-0.5 truncate text-[13px] font-semibold text-lais-text"
                >
                  {{ confidenceLabel(item.confianca) }}
                </dd>
              </div>
            </dl>
            <footer class="border-t border-lais-border-soft px-4 py-3">
              <button
                type="button"
                class="inline-flex items-center gap-2 text-[13px] font-semibold text-lais-primary"
                @click.stop="openDetail(item)"
              >
                <span class="i-lucide-file-search size-4" aria-hidden="true" />
                Ver detalhe e diff
              </button>
            </footer>
          </article>
        </template>
      </div>

      <div class="mt-4 hidden min-w-0 lg:block">
        <LaisCard density="flush" class="min-w-0">
          <div
            class="w-full max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain"
            :aria-busy="isLoading"
          >
            <table
              class="min-w-[1520px] divide-y divide-lais-border-soft text-left text-[13px]"
            >
              <thead class="bg-[#fbfaf8] shadow-lais-soft">
                <tr>
                  <th
                    v-for="column in tableColumns"
                    :key="column.key"
                    class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase text-lais-text-muted"
                  >
                    <button
                      v-if="column.sortBy"
                      type="button"
                      class="inline-flex items-center gap-1 rounded-full px-2 py-1 transition hover:bg-white hover:text-lais-primary"
                      @click="sortByColumn(column)"
                    >
                      {{ column.label }}
                      <span
                        class="size-3.5"
                        :class="sortIcon(column)"
                        aria-hidden="true"
                      />
                    </button>
                    <span v-else class="px-2 py-1">{{ column.label }}</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-lais-border-soft bg-white">
                <tr v-if="isLoading">
                  <td
                    :colspan="tableColumns.length"
                    class="px-4 py-14 text-center text-lais-text-muted"
                  >
                    <span
                      class="i-lucide-loader-circle mx-auto mb-2 block size-6 animate-spin text-lais-primary"
                      aria-hidden="true"
                    />
                    Carregando mudanças...
                  </td>
                </tr>
                <tr v-else-if="isListUnavailable">
                  <td
                    :colspan="tableColumns.length"
                    class="px-4 py-14 text-center"
                  >
                    <span
                      class="i-lucide-cloud-off mx-auto mb-2 block size-6 text-amber-700"
                      aria-hidden="true"
                    />
                    <strong class="block text-[14px] text-lais-text">
                      Serviço de mudanças indisponível
                    </strong>
                    <span
                      class="mx-auto mt-1 block max-w-xl text-[13px] font-medium text-lais-text-muted"
                    >
                      {{ errorMessage }}
                    </span>
                    <button
                      type="button"
                      class="mt-3 inline-flex items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="isLoading"
                      @click="refreshAll"
                    >
                      <span
                        class="i-lucide-refresh-cw size-3.5"
                        aria-hidden="true"
                      />
                      Atualizar listagem
                    </button>
                  </td>
                </tr>
                <tr v-else-if="!items.length">
                  <td
                    :colspan="tableColumns.length"
                    class="px-4 py-14 text-center text-lais-text-muted"
                  >
                    Nenhuma mudança encontrada com os filtros atuais.
                  </td>
                </tr>
                <template v-else>
                  <tr
                    v-for="item in items"
                    :key="item.id"
                    role="button"
                    tabindex="0"
                    class="group cursor-pointer hover:bg-lais-primary-50/20 focus:bg-lais-primary-50/30 focus:outline-none"
                    :aria-label="`Ver detalhe da mudança ${item.id}`"
                    @click="openDetail(item)"
                    @keydown.enter.prevent="openDetail(item)"
                    @keydown.space.prevent="openDetail(item)"
                  >
                    <td
                      v-for="column in tableColumns"
                      :key="column.key"
                      class="max-w-[280px] px-4 py-3 align-top text-lais-text"
                    >
                      <div
                        v-if="column.key === 'mudanca'"
                        class="max-w-[340px]"
                      >
                        <div class="flex flex-wrap items-center gap-2">
                          <span
                            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-bold text-lais-primary"
                          >
                            {{ tipoLabel(item.tipo_alteracao) }}
                          </span>
                          <span
                            class="text-[11px] font-semibold text-lais-text-muted"
                          >
                            #{{ item.id }} · E-mail #{{ item.email_id }}
                          </span>
                        </div>
                        <p class="mt-2 line-clamp-2 text-[13px] leading-snug">
                          {{
                            display(item.descricao_alteracao, 'Sem descrição')
                          }}
                        </p>
                        <button
                          type="button"
                          class="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-lais-primary opacity-0 transition group-hover:opacity-100 group-focus:opacity-100"
                          @click.stop="openDetail(item)"
                        >
                          <span
                            class="i-lucide-file-search size-3.5"
                            aria-hidden="true"
                          />
                          Ver detalhe
                        </button>
                      </div>
                      <div
                        v-else-if="column.key === 'solicitante'"
                        class="max-w-[220px]"
                      >
                        <p class="truncate font-semibold">
                          {{ requesterName(item) }}
                        </p>
                        <p
                          v-if="requesterEmail(item)"
                          class="mt-0.5 truncate text-[11px] text-lais-text-muted"
                        >
                          {{ requesterEmail(item) }}
                        </p>
                      </div>
                      <span
                        v-else-if="column.key === 'status'"
                        class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                        :class="statusTone(item.status)"
                      >
                        {{ statusLabel(item.status) }}
                      </span>
                      <span
                        v-else-if="column.key === 'audit_status'"
                        class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                        :class="auditTone(item.audit_status)"
                      >
                        {{ auditLabel(item.audit_status) }}
                      </span>
                      <span
                        v-else-if="column.key === 'prioridade'"
                        class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
                        :class="prioridadeTone(item.prioridade)"
                      >
                        {{ prioridadeLabel(item.prioridade) }}
                      </span>
                      <span v-else-if="column.key === 'confianca'">
                        {{ confidenceLabel(item.confianca) }}
                      </span>
                      <span v-else-if="column.key === 'data_solicitacao'">
                        {{ formatDate(item.data_solicitacao) }}
                      </span>
                      <span v-else-if="column.key === 'extraido_em'">
                        {{ formatDate(item.extraido_em) }}
                      </span>
                      <span
                        v-else
                        class="line-clamp-2"
                        :title="display(item[column.key])"
                      >
                        {{ display(item[column.key]) }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </LaisCard>
      </div>

      <div
        class="mt-4 flex flex-col gap-3 rounded-lais border border-lais-border bg-white px-4 py-3 text-[13px] font-semibold text-lais-text-muted shadow-lais-card sm:flex-row sm:items-center sm:justify-between"
      >
        <span>
          Página {{ number(pageMeta.page) }} de
          {{ number(pageMeta.totalPages) }}
        </span>
        <div class="flex items-center gap-2">
          <button
            type="button"
            :class="compactButtonClass"
            :disabled="pageMeta.page <= 1 || isLoading"
            @click="goToPage(pageMeta.page - 1)"
          >
            <span class="i-lucide-chevron-left size-4" aria-hidden="true" />
            Anterior
          </button>
          <button
            type="button"
            :class="compactButtonClass"
            :disabled="pageMeta.page >= pageMeta.totalPages || isLoading"
            @click="goToPage(pageMeta.page + 1)"
          >
            Próxima
            <span class="i-lucide-chevron-right size-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </main>

    <LaisModal
      :show="isDetailModalOpen"
      :title="selectedMudancaTitle"
      max-width-class="max-w-[760px]"
      @close="closeDetail"
    >
      <div v-if="selectedMudanca" class="grid gap-5">
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="rounded-full bg-lais-primary-50 px-2.5 py-1 text-[11px] font-bold text-lais-primary"
          >
            {{ tipoLabel(selectedMudanca.tipo_alteracao) }}
          </span>
          <span
            class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
            :class="statusTone(selectedMudanca.status)"
          >
            {{ statusLabel(selectedMudanca.status) }}
          </span>
          <span
            class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
            :class="auditTone(selectedMudanca.audit_status)"
          >
            {{ auditLabel(selectedMudanca.audit_status) }}
          </span>
          <span
            class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
            :class="prioridadeTone(selectedMudanca.prioridade)"
          >
            {{ prioridadeLabel(selectedMudanca.prioridade) }}
          </span>
        </div>

        <div
          v-if="detailErrorMessage"
          class="rounded-lais border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold text-amber-800"
        >
          {{ detailErrorMessage }}
        </div>

        <div
          v-if="actionErrorMessage"
          class="rounded-lais border border-red-100 bg-red-50 px-4 py-3 text-[13px] font-semibold text-red-700"
          role="alert"
        >
          {{ actionErrorMessage }}
        </div>

        <div
          v-if="actionSuccessMessage"
          class="rounded-lais border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-800"
          role="status"
        >
          {{ actionSuccessMessage }}
        </div>

        <div
          v-if="isDetailLoading"
          class="rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-8 text-center text-[13px] font-semibold text-lais-text-muted"
        >
          <span
            class="i-lucide-loader-circle mx-auto mb-2 block size-5 animate-spin text-lais-primary"
            aria-hidden="true"
          />
          Carregando detalhe...
        </div>

        <section
          class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p
                class="text-[11px] font-bold uppercase tracking-wide text-lais-text-muted"
              >
                Proveniência e regra
              </p>
              <p class="mt-1 text-[13px] leading-relaxed text-lais-text">
                {{ ruleExplanation }}
              </p>
            </div>
            <span
              class="inline-flex items-center gap-1 rounded-full border border-lais-border bg-lais-bg-page px-2.5 py-1 text-[11px] font-semibold text-lais-text-muted"
            >
              <span class="i-lucide-shield-check size-3.5" aria-hidden="true" />
              {{ confidenceLabel(selectedMudanca.confianca) }}
            </span>
          </div>
          <dl class="grid gap-3 sm:grid-cols-4">
            <div
              v-for="row in provenanceRows"
              :key="row.label"
              class="min-w-0 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
            >
              <dt
                class="text-[11px] font-bold uppercase tracking-wide text-lais-text-muted"
              >
                {{ row.label }}
              </dt>
              <dd
                class="mt-1 break-words text-[13px] font-semibold text-lais-text"
              >
                {{ row.value }}
              </dd>
            </div>
          </dl>
        </section>

        <section
          class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
        >
          <div>
            <p
              class="text-[11px] font-bold uppercase tracking-wide text-lais-text-muted"
            >
              Descrição extraída
            </p>
            <p class="mt-1 text-[14px] leading-relaxed text-lais-text">
              {{
                display(selectedMudanca.descricao_alteracao, 'Sem descrição')
              }}
            </p>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <div class="rounded-lais-sm border border-red-100 bg-red-50/60 p-4">
              <p
                class="text-[11px] font-bold uppercase tracking-wide text-red-700"
              >
                Antes
              </p>
              <p class="mt-2 whitespace-pre-wrap text-[14px] text-lais-text">
                {{
                  display(selectedMudanca.valor_anterior, 'Sem valor anterior')
                }}
              </p>
            </div>
            <div
              class="rounded-lais-sm border border-emerald-100 bg-emerald-50/70 p-4"
            >
              <p
                class="text-[11px] font-bold uppercase tracking-wide text-emerald-700"
              >
                Depois
              </p>
              <p class="mt-2 whitespace-pre-wrap text-[14px] text-lais-text">
                {{ display(selectedMudanca.valor_novo, 'Sem valor novo') }}
              </p>
            </div>
          </div>
        </section>

        <section
          class="grid gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-4 sm:grid-cols-2"
        >
          <div v-for="row in detailMetaRows" :key="row.label" class="min-w-0">
            <p
              class="text-[11px] font-bold uppercase tracking-wide text-lais-text-muted"
            >
              {{ row.label }}
            </p>
            <p
              class="mt-1 break-words text-[13px] font-semibold text-lais-text"
            >
              {{ row.value }}
            </p>
          </div>
        </section>

        <LaisPayloadViewer
          v-if="hasDetailRawPayload"
          :payload="detailRawPayload"
          title="Payload extra redigido"
          schema-version="mudancas-runtime-v1"
          :redacted-keys="detailPayloadRedactedKeys"
          :can-copy="false"
          :collapsed="false"
        />

        <section
          class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p
                class="text-[11px] font-bold uppercase tracking-wide text-lais-text-muted"
              >
                Histórico de ações
              </p>
              <p class="mt-1 text-[13px] text-lais-text-muted">
                Últimos registros desta mudança.
              </p>
            </div>
            <span
              v-if="isActionLedgerLoading"
              class="inline-flex items-center gap-2 text-[12px] font-semibold text-lais-text-muted"
            >
              <span
                class="i-lucide-loader-circle size-3.5 animate-spin"
                aria-hidden="true"
              />
              Carregando
            </span>
          </div>
          <div v-if="!isActionLedgerLoading && !actionLedger.length">
            <p class="text-[13px] font-semibold text-lais-text-muted">
              Nenhuma ação registrada para esta mudança.
            </p>
          </div>
          <ol v-else class="grid gap-2">
            <li
              v-for="entry in actionLedger"
              :key="entry.id"
              class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
            >
              <div class="flex flex-wrap items-center gap-2">
                <strong class="text-[13px] text-lais-text">
                  {{ actionLabel(entry.action) }}
                </strong>
                <span
                  class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1"
                  :class="actionStatusTone(entry.status)"
                >
                  {{ actionStatusLabel(entry.status) }}
                </span>
                <span class="text-[11px] font-semibold text-lais-text-muted">
                  {{ formatDate(entry.occurred_at) }} · ator #{{
                    entry.actor_id
                  }}
                </span>
              </div>
              <p class="mt-2 text-[13px] leading-relaxed text-lais-text">
                {{ display(entry.reason, 'Sem motivo registrado') }}
              </p>
              <p
                v-if="entry.error_message"
                class="mt-1 text-[12px] font-semibold text-red-700"
              >
                {{ entry.error_message }}
              </p>
            </li>
          </ol>
        </section>
      </div>

      <template #footer>
        <div
          class="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isActionSubmitting || isDetailLoading"
              @click="openActionModal('comment')"
            >
              <span
                class="i-lucide-message-square-plus size-4"
                aria-hidden="true"
              />
              Comentar
            </button>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-red-200 bg-white px-3 text-[13px] font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="
                !canDecideSelectedMudanca ||
                isActionSubmitting ||
                isDetailLoading
              "
              @click="openActionModal('reject')"
            >
              <span class="i-lucide-x-circle size-4" aria-hidden="true" />
              Recusar
            </button>
            <button
              type="button"
              class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-emerald-600 bg-emerald-600 px-3 text-[13px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="
                !canDecideSelectedMudanca ||
                isActionSubmitting ||
                isDetailLoading
              "
              @click="openActionModal('accept')"
            >
              <span class="i-lucide-check-circle-2 size-4" aria-hidden="true" />
              Aceitar
            </button>
          </div>
          <button
            type="button"
            class="h-10 px-4 text-[13px] font-semibold text-lais-text-body hover:text-lais-text"
            @click="closeDetail"
          >
            Fechar
          </button>
        </div>
      </template>
    </LaisModal>

    <LaisActionReasonModal
      :show="actionModal.show"
      :title="actionModalCopy.title"
      :description="actionModalCopy.description"
      :consequence="actionModalCopy.consequence"
      :reason-label="actionModalCopy.reasonLabel"
      :reason-placeholder="actionModalCopy.reasonPlaceholder"
      :acknowledge-label="actionModalCopy.acknowledgeLabel"
      :confirm-text="actionModalCopy.confirmText"
      :confirm-variant="actionModalCopy.confirmVariant"
      :is-loading="isActionSubmitting"
      :max-reason-length="500"
      @confirm="performMudancaAction"
      @close="closeActionModal"
    />
  </section>
</template>
